"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";

import { trackEvent } from "@/lib/analytics";
import { useImpression } from "@/lib/use-impression";
import {
  clearSession,
  newSessionId,
  saveSession,
} from "@/app/zenyra/lib/session-storage";
import {
  getSessionStatus,
  hasRecommendation,
  reentryTarget,
  useZenyraStatus,
  type ZenyraStatus,
} from "@/app/zenyra/lib/session-status";
import { mockOpening } from "@/app/zenyra/lib/mock-ai";
import { ACCESS_DENIED_MESSAGE, isAllowedName } from "@/app/zenyra/lib/access";
import {
  MAX_MESSAGE_LENGTH,
  SESSION_SCHEMA_VERSION,
} from "@/app/zenyra/lib/types";

type Variant = "card" | "inline" | "compact";

type Props = {
  /** Required topic slug for tracking + pre-fill (e.g. "anxietate"). */
  topic?: string;
  /** Where the click came from for analytics. */
  location: string;
  /** Override the heading text. */
  title?: string;
  /** Override the subhead text. */
  subtitle?: string;
  /** Visual size / placement. */
  variant?: Variant;
  /** Optional pre-filled first user message that Zenyra will receive. */
  prefill?: string;
  /** Override the button/link label. */
  ctaLabel?: string;
  className?: string;
};

const DEFAULT_TITLE = "Vorbește 20 de minute cu Zenyra";
const DEFAULT_SUBTITLE =
  "Asistenta AI Mindtale. Sesiune de cunoaștere gratuită. La final îți recomandă terapeutul potrivit.";

function buildHref(topic?: string, prefill?: string): string {
  const params = new URLSearchParams();
  if (topic) params.set("topic", topic);
  if (prefill) params.set("prefill", prefill.slice(0, 500));
  const qs = params.toString();
  return qs ? `/zenyra?${qs}` : "/zenyra";
}

export default function ZenyraCta({
  topic,
  location,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  variant = "card",
  prefill,
  ctaLabel,
  className = "",
}: Props) {
  const href = buildHref(topic, prefill);
  const rootRef = useRef<HTMLElement | null>(null);
  useImpression(rootRef, {
    event: "zenyra_cta_impression",
    params: { source: location, topic: topic ?? null, variant },
    threshold: 0.5,
  });
  const onClick = () => {
    trackEvent("zenyra_cta_click", {
      location,
      source: location,
      topic: topic ?? null,
      variant,
    });
  };

  // Re-entry: if the user already has a saved conversation, point the CTA back
  // to it (resume or view the recommendation) instead of starting fresh.
  const status = useZenyraStatus();
  const reentry = reentryTarget(status);
  const effectiveHref = reentry?.href ?? href;
  const reentryClick = () => {
    if (!reentry) {
      onClick();
      return;
    }
    trackEvent(
      reentry.kind === "active" ? "zenyra_cta_resume" : "zenyra_cta_view_rec",
      { location, source: location, topic: topic ?? null, variant },
    );
  };

  if (variant === "compact") {
    return (
      <Link
        ref={rootRef as React.RefObject<HTMLAnchorElement>}
        href={effectiveHref}
        onClick={reentryClick}
        className={`group inline-flex items-center gap-2 rounded-lg border border-[#264653]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#264653] transition-colors hover:border-[#264653]/40 ${className}`}
      >
        <Image
          src="/Zenyra.svg"
          alt=""
          width={20}
          height={20}
          className="size-5"
        />
        <span>{reentry ? reentry.buttonLabel : ctaLabel ?? "Vorbește cu Zenyra"}</span>
        <ArrowRight className="size-3.5 text-[#2C969B] transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  if (variant === "inline") {
    if (reentry) {
      return (
        <div
          ref={rootRef as React.RefObject<HTMLDivElement>}
          className={`overflow-hidden rounded-lg border border-[#264653]/12 bg-white ${className}`}
        >
          <Link
            href={effectiveHref}
            onClick={reentryClick}
            className="group flex items-center gap-3 p-3 transition hover:bg-[#FFF9F5]"
          >
            <Image
              src="/Zenyra.svg"
              alt=""
              width={40}
              height={40}
              className="size-10 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#264653]">
                {reentry.peekTitle}
              </p>
              <p className="mt-0.5 truncate text-xs text-[#264653]/70">
                {reentry.kind === "active"
                  ? "Ai o conversație neterminată. Reia-o de unde ai rămas."
                  : "Vezi terapeutul din conversația ta cu Zenyra"}
              </p>
            </div>
            <ArrowRight className="size-4 shrink-0 text-[#2C969B] transition group-hover:translate-x-0.5" />
          </Link>
          <Link
            href="/zenyra"
            onClick={() =>
              trackEvent("zenyra_cta_new_conversation", {
                location,
                topic: topic ?? null,
              })
            }
            className="block border-t border-[#264653]/10 px-3 py-2 text-center text-xs font-medium text-[#264653]/65 transition-colors hover:text-[#264653]"
          >
            Începe o conversație nouă
          </Link>
        </div>
      );
    }
    return (
      <Link
        ref={rootRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        onClick={onClick}
        className={`group flex items-center gap-3 rounded-lg border border-[#264653]/12 bg-white p-3 transition-colors hover:border-[#264653]/30 ${className}`}
      >
        <Image
          src="/Zenyra.svg"
          alt=""
          width={40}
          height={40}
          className="size-10 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#264653]">{title}</p>
          <p className="mt-0.5 truncate text-xs text-[#264653]/70">{subtitle}</p>
        </div>
        <ArrowRight className="size-4 shrink-0 text-[#2C969B] transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  // card (default)
  return (
    <div
      ref={rootRef as React.RefObject<HTMLDivElement>}
      className={`relative overflow-hidden rounded-lg border border-[#264653]/12 bg-white p-5 sm:p-7 ${className}`}
    >

      <div className="relative flex flex-col items-start gap-4 sm:flex-row sm:items-start">
        <div className="flex items-center gap-3 sm:flex-col sm:items-start">
          <Image
            src="/Zenyra.svg"
            alt="Zenyra"
            width={56}
            height={56}
            className="size-14 shrink-0"
          />
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2C969B]">
            <Sparkles className="size-2.5" />
            Sesiune gratuită
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-[19px] font-medium text-[#264653] sm:text-[21px]">
            {title}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-[#264653]/75">
            {subtitle}
          </p>

          {prefill ? (
            <InlineStart
              prefill={prefill}
              topic={topic}
              location={location}
              ctaLabel={ctaLabel}
              href={href}
              onTrack={onClick}
            />
          ) : (
            <>
              <Link
                href={effectiveHref}
                onClick={reentryClick}
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-lg bg-[#DC7050] px-6 text-[14px] font-semibold text-white transition-colors hover:bg-[#c25c3e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2C969B]"
              >
                {reentry ? reentry.buttonLabel : ctaLabel ?? "Începe sesiunea"}
                <ArrowRight className="size-4" />
              </Link>
              <p className="mt-2 text-[11px] text-[#264653]/65">
                {reentry
                  ? "Din conversația ta cu Zenyra, salvată pe acest dispozitiv."
                  : "20 min · fără cont · privat. Conversația rămâne pe dispozitivul tău."}
              </p>
              {reentry && (
                <Link
                  href="/zenyra"
                  onClick={() =>
                    trackEvent("zenyra_cta_new_conversation", {
                      location,
                      topic: topic ?? null,
                    })
                  }
                  className="mt-1 inline-block text-[11px] font-medium text-[#264653]/65 underline underline-offset-2 hover:text-[#264653]"
                >
                  Începe o conversație nouă
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InlineStart({
  prefill,
  topic,
  location,
  ctaLabel,
  href,
  onTrack,
}: {
  prefill: string;
  topic?: string;
  location: string;
  ctaLabel?: string;
  href: string;
  onTrack: () => void;
}) {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [status, setStatus] = useState<ZenyraStatus | null>(null);
  const [confirmingNew, setConfirmingNew] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setMounted(true);
    setStatus(getSessionStatus());
  }, []);

  const session =
    status && (status.kind === "active" || status.kind === "ended")
      ? status.session
      : null;
  const reentry = reentryTarget(status);
  const sessionHasRec = session ? hasRecommendation(session) : false;

  const startNew = () => {
    clearSession();
    setStatus({ kind: "none" });
    setConfirmingNew(false);
    trackEvent("zenyra_cta_resume_dismissed", {
      location,
      topic: topic ?? null,
    });
  };
  const requestNew = () => {
    if (sessionHasRec) {
      setConfirmingNew(true);
      return;
    }
    startNew();
  };

  if (mounted && confirmingNew) {
    return (
      <div className="mt-4 rounded-lg border border-[#264653]/12 bg-[#FFF9F5] p-4">
        <p className="text-sm font-semibold text-[#264653]">
          Pierzi recomandarea salvată
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-[#264653]/70">
          O conversație nouă înlocuiește recomandarea pe care o ai salvată pe
          acest dispozitiv. Sigur?
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={startNew}
            className="inline-flex h-11 w-fit items-center justify-center rounded-lg bg-[#DC7050] px-6 text-[14px] font-semibold text-white transition-colors hover:bg-[#c25c3e]"
          >
            Da, începe alta
          </button>
          <button
            type="button"
            onClick={() => setConfirmingNew(false)}
            className="text-[12px] text-[#264653]/70 underline underline-offset-2 hover:text-[#264653]"
          >
            Anulează
          </button>
        </div>
      </div>
    );
  }

  if (mounted && session && reentry) {
    const userMsgCount = session.messages.filter(
      (m) => m.role === "user",
    ).length;
    const isEnded = status?.kind === "ended";
    return (
      <div className="mt-4 flex flex-col gap-2">
        <p className="text-[12px] leading-relaxed text-[#264653]/75">
          {isEnded ? (
            <>
              Ai vorbit deja cu Zenyra,{" "}
              <span className="font-semibold text-[#264653]">{session.name}</span>.
              Recomandarea din conversația ta te așteaptă.
            </>
          ) : (
            <>
              Bună,{" "}
              <span className="font-semibold text-[#264653]">{session.name}</span>.
              Ai o conversație neterminată cu Zenyra
              {userMsgCount > 0
                ? ` (${userMsgCount} ${userMsgCount === 1 ? "mesaj" : "mesaje"})`
                : ""}
              .
            </>
          )}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => {
              onTrack();
              trackEvent(
                isEnded ? "zenyra_cta_view_rec" : "zenyra_cta_resume",
                { location, topic: topic ?? null },
              );
              router.push(reentry.href);
            }}
            className="inline-flex h-11 w-fit items-center gap-2 rounded-lg bg-[#DC7050] px-6 text-[14px] font-semibold text-white transition-colors hover:bg-[#c25c3e]"
          >
            {isEnded ? "Vezi recomandarea ta" : "Reia conversația"}
            <ArrowRight className="size-4" />
          </button>
          <button
            type="button"
            onClick={requestNew}
            className="text-[12px] text-[#264653]/70 underline underline-offset-2 hover:text-[#264653]"
          >
            Începe o sesiune nouă
          </button>
        </div>
      </div>
    );
  }

  const trimmed = name.trim();
  const canSubmit = trimmed.length >= 2 && !submitting;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!isAllowedName(trimmed)) {
      setErr(ACCESS_DENIED_MESSAGE);
      return;
    }
    setSubmitting(true);
    onTrack();

    const safeName = trimmed.slice(0, 40);
    const safeMsg = prefill.slice(0, MAX_MESSAGE_LENGTH);
    const sid = newSessionId();
    const now = Date.now();

    saveSession({
      v: SESSION_SCHEMA_VERSION,
      sessionId: sid,
      name: safeName,
      startedAt: now,
      firstUserMsgAt: now + 1,
      pendingFirstSend: true,
      messages: [
        {
          id: `m_${now}_0`,
          role: "assistant",
          content: mockOpening(safeName),
          createdAt: now,
          kind: "contract",
        },
        {
          id: `m_${now}_1`,
          role: "user",
          content: safeMsg,
          createdAt: now + 1,
        },
      ],
    });

    trackEvent("sesiune_ai_started", {
      source: location,
      topic: topic ?? null,
      via: "zenyra_cta_inline",
    });
    trackEvent("sesiune_ai_first_user_msg", {
      source: location,
      ms_since_start: 0,
    });

    router.push("/zenyra/chat");
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-2" noValidate>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-[#264653]/65">
        Cum vrei să-ți spunem?
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value.slice(0, 40));
            if (err) setErr(null);
          }}
          placeholder="Numele tău"
          maxLength={40}
          autoComplete="given-name"
          className="h-11 flex-1 rounded-lg border border-[#264653]/15 bg-white px-4 text-sm text-[#264653] placeholder:text-[#264653]/35 focus:border-[#2C969B] focus:outline-none focus:ring-2 focus:ring-[#2C969B]/20"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#DC7050] px-6 text-[14px] font-semibold text-white transition-colors hover:bg-[#c25c3e] disabled:cursor-not-allowed disabled:bg-[#DC7050]/40"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <>
              {ctaLabel ?? "Trimite Zenyrei"}
              <ArrowRight className="size-4" />
            </>
          )}
        </button>
      </div>
      {err && <p className="text-[11px] text-[#DC7050]">{err}</p>}
      <p className="text-[11px] text-[#264653]/65">
        Mesajul tău către Zenyra:{" "}
        <span className="italic">"{prefill.slice(0, 160)}{prefill.length > 160 ? "…" : ""}"</span>
      </p>
      <p className="text-[11px] text-[#264653]/55">
        20 min · fără cont · privat. Conversația rămâne pe dispozitivul tău.{" "}
        <Link
          href={href}
          className="underline underline-offset-2 hover:text-[#264653]"
        >
          Vezi detalii
        </Link>
      </p>
    </form>
  );
}
